// src/pages/admin/EditorAccessSettings.jsx
import { useEffect, useState } from "react";
import { api } from "../../services/api";
import { Monitor, ToggleLeft, ToggleRight, Save, RefreshCw } from "lucide-react";

const C = {
  bg: "#F4F5F7",
  card: "#ffffff",
  dark: "#0c1118",
  amber: "#111111",
  amberBg: "rgba(0,0,0,0.06)",
  amberBorder: "rgba(0,0,0,0.12)",
  border: "#E8EAED",
  text: "#374151",
  muted: "#6B7280",
  success: "#16a34a",
  successBg: "#f0fdf4",
  error: "#dc2626",
  errorBg: "#fef2f2",
};

const S = {
  page: {
    fontFamily: "'DM Sans', sans-serif",
  },
  header: { marginBottom: 28 },
  title: {
    margin: 0,
    fontSize: 24,
    fontWeight: 700,
    color: C.dark,
    fontFamily: "'DM Sans', sans-serif",
  },
  subtitle: { margin: "4px 0 0", fontSize: 14, color: C.muted },
  card: {
    background: C.card,
    border: `1px solid ${C.border}`,
    borderRadius: 12,
    overflow: "hidden",
    marginBottom: 20,
  },
  cardHeader: {
    background: C.dark,
    padding: "14px 20px",
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  cardTitle: {
    margin: 0,
    fontSize: 13,
    fontWeight: 700,
    color: "#ffffff",
    letterSpacing: 0.5,
    fontFamily: "'DM Sans', sans-serif",
  },
  cardBody: { padding: "24px 20px" },
};

function inject() {
  if (typeof document === "undefined") return;
  if (!document.getElementById("eas-fonts")) {
    const l = document.createElement("link");
    l.id = "eas-fonts";
    l.rel = "stylesheet";
    l.href =
      "https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&display=swap";
    document.head.appendChild(l);
  }
}
inject();

const VIEW_OPTIONS = [
  {
    value: "3d",
    label: "3D View Only",
    desc: "Guests can only access the 3D editor viewport.",
  },
  {
    value: "2d",
    label: "2D View Only",
    desc: "Guests can only access the 2D floor plan viewport.",
  },
  {
    value: "both",
    label: "Both Views",
    desc: "Guests can switch between 3D and 2D viewports freely.",
  },
];

export default function EditorAccessSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null); // { type: 'ok'|'err', msg }

  const [guestAccessEnabled, setGuestAccessEnabled] = useState(false);
  const [guestAllowedViews, setGuestAllowedViews] = useState("3d");

  // persisted values — to know if dirty
  const [saved, setSaved] = useState({ guestAccessEnabled: false, guestAllowedViews: "3d" });

  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const d = await api.get("/api/admin/editor-settings");
        if (dead) return;
        setGuestAccessEnabled(d.guestAccessEnabled ?? false);
        setGuestAllowedViews(d.guestAllowedViews ?? "3d");
        setSaved({ guestAccessEnabled: d.guestAccessEnabled ?? false, guestAllowedViews: d.guestAllowedViews ?? "3d" });
      } catch (e) {
        if (!dead) showToast("err", e?.message || "Failed to load settings.");
      } finally {
        if (!dead) setLoading(false);
      }
    })();
    return () => { dead = true; };
  }, []);

  function showToast(type, msg) {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 3500);
  }

  async function handleSave() {
    setSaving(true);
    try {
      const d = await api.put("/api/admin/editor-settings", { guestAccessEnabled, guestAllowedViews });
      setSaved({ guestAccessEnabled: d.guestAccessEnabled, guestAllowedViews: d.guestAllowedViews });
      showToast("ok", "Settings saved successfully.");
    } catch (e) {
      showToast("err", e?.message || "Failed to save settings.");
    } finally {
      setSaving(false);
    }
  }

  const isDirty =
    guestAccessEnabled !== saved.guestAccessEnabled ||
    guestAllowedViews !== saved.guestAllowedViews;

  if (loading) {
    return (
      <div style={{ ...S.page, display: "flex", alignItems: "center", justifyContent: "center", minHeight: 300 }}>
        <RefreshCw size={22} style={{ color: C.muted, animation: "spin 1s linear infinite" }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={S.page}>
      {/* Header */}
      <div style={S.header}>
        <h1 style={S.title}>3D Editor Settings</h1>
        <p style={S.subtitle}>
          Control guest (unauthenticated) access to the global 3D editor at{" "}
          <code style={{ fontSize: 12, background: "#f3f4f6", padding: "2px 6px", borderRadius: 4 }}>
            app.mydesignerplus.com
          </code>
        </p>
      </div>

      {/* Toast */}
      {toast && (
        <div
          style={{
            marginBottom: 20,
            padding: "12px 16px",
            borderRadius: 10,
            fontSize: 13,
            fontWeight: 500,
            display: "flex",
            alignItems: "center",
            gap: 8,
            background: toast.type === "ok" ? C.successBg : C.errorBg,
            border: `1px solid ${toast.type === "ok" ? "#bbf7d0" : "#fecaca"}`,
            color: toast.type === "ok" ? C.success : C.error,
          }}
        >
          {toast.type === "ok" ? "✓" : "✗"} {toast.msg}
        </div>
      )}

      {/* Guest Access Toggle */}
      <div style={S.card}>
        <div style={S.cardHeader}>
          <Monitor size={15} style={{ color: "#ffffff" }} />
          <h3 style={S.cardTitle}>Guest Access</h3>
        </div>
        <div style={S.cardBody}>
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 20,
            }}
          >
            <div style={{ flex: 1 }}>
              <p style={{ margin: "0 0 4px", fontSize: 15, fontWeight: 600, color: C.dark }}>
                Allow guest users to access the 3D Editor
              </p>
              <p style={{ margin: 0, fontSize: 13, color: C.muted, lineHeight: 1.5 }}>
                When enabled, visitors to <strong>app.mydesignerplus.com</strong> can use the 3D Editor
                without logging in. They will see a "Sign Up" button in the toolbar and their design
                is preserved until they register.
              </p>
            </div>

            <button
              onClick={() => setGuestAccessEnabled((v) => !v)}
              title={guestAccessEnabled ? "Disable guest access" : "Enable guest access"}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 0,
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              {guestAccessEnabled ? (
                <ToggleRight size={36} style={{ color: C.amber }} />
              ) : (
                <ToggleLeft size={36} style={{ color: C.muted }} />
              )}
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  fontFamily: "'DM Sans', sans-serif",
                  letterSpacing: "0.06em",
                  color: guestAccessEnabled ? C.amber : C.muted,
                  whiteSpace: "nowrap",
                }}
              >
                {guestAccessEnabled ? "Enabled" : "Disabled"}
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* View Restriction — shown even when disabled so admin can pre-configure */}
      <div style={{ ...S.card, opacity: guestAccessEnabled ? 1 : 0.55, transition: "opacity 0.2s" }}>
        <div style={S.cardHeader}>
          <Monitor size={15} style={{ color: "#ffffff" }} />
          <h3 style={S.cardTitle}>Guest View Access</h3>
        </div>
        <div style={S.cardBody}>
          <p style={{ margin: "0 0 16px", fontSize: 13, color: C.muted }}>
            Choose which editor viewports guest users can access.
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {VIEW_OPTIONS.map((opt) => {
              const selected = guestAllowedViews === opt.value;
              return (
                <label
                  key={opt.value}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 12,
                    padding: "14px 16px",
                    borderRadius: 10,
                    border: `1.5px solid ${selected ? C.amber : C.border}`,
                    background: selected ? C.amberBg : C.card,
                    cursor: guestAccessEnabled ? "pointer" : "not-allowed",
                    transition: "all 0.14s",
                  }}
                >
                  <input
                    type="radio"
                    name="guestAllowedViews"
                    value={opt.value}
                    checked={selected}
                    disabled={!guestAccessEnabled}
                    onChange={() => setGuestAllowedViews(opt.value)}
                    style={{
                      marginTop: 2,
                      accentColor: C.amber,
                      width: 16,
                      height: 16,
                      flexShrink: 0,
                      cursor: guestAccessEnabled ? "pointer" : "not-allowed",
                    }}
                  />
                  <div>
                    <p style={{ margin: "0 0 2px", fontSize: 14, fontWeight: 600, color: selected ? C.dark : C.text }}>
                      {opt.label}
                    </p>
                    <p style={{ margin: 0, fontSize: 12, color: C.muted, lineHeight: 1.4 }}>
                      {opt.desc}
                    </p>
                  </div>
                </label>
              );
            })}
          </div>
        </div>
      </div>

      {/* Save button */}
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button
          onClick={handleSave}
          disabled={saving || !isDirty}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "10px 24px",
            borderRadius: 10,
            border: "none",
            cursor: saving || !isDirty ? "not-allowed" : "pointer",
            background: saving || !isDirty ? "#e5e7eb" : C.dark,
            color: saving || !isDirty ? C.muted : "#fff",
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 14,
            fontWeight: 600,
            transition: "all 0.15s",
            boxShadow: saving || !isDirty ? "none" : "0 2px 8px rgba(0,0,0,0.15)",
          }}
          onMouseEnter={(e) => {
            if (!saving && isDirty) e.currentTarget.style.background = "#1c2a36";
          }}
          onMouseLeave={(e) => {
            if (!saving && isDirty) e.currentTarget.style.background = C.dark;
          }}
        >
          {saving ? (
            <>
              <RefreshCw size={15} style={{ animation: "spin 1s linear infinite" }} />
              Saving…
            </>
          ) : (
            <>
              <Save size={15} />
              Save Changes
            </>
          )}
        </button>
      </div>
    </div>
  );
}
